import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import { Modal, Button, Skeleton, Tag } from "antd"

import { fetchUserClientAssignments } from "./usersSlice"
import { titleize } from "../../lib/string"

/// Which clients a given user is assigned to, opened on demand from a table cell.
///
/// Mount this once and toggle `open` -- it caches every successful lookup by user id for the life
/// of the page, so reopening the same author is instant. Unmounting it between opens would throw
/// that cache away.
///
/// @param {*} user    the row's user object -- needs `id`, uses `first_name`/`last_name`/`email` for
///                    the header so it can render before the request comes back
/// @param {*} open    whether the modal is showing
/// @param {*} onClose called when the user dismisses it
export default function UserClientAssignmentsModal({ user, open, onClose }) {
    const dispatch = useDispatch()

    /// successes only -- an error is left out so reopening retries instead of showing a stale failure
    const [assignmentsByUserId, setAssignmentsByUserId] = useState({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const assignments = user?.id ? assignmentsByUserId[user.id] : null

    /// whichever user is on screen now -- `loading` and `error` are shared, so a request for a row
    /// the user has since navigated away from must not write to them
    const currentRequest = useRef(null)

    const load = async (userId) => {
        currentRequest.current = userId
        setLoading(true)
        setError(null)

        try {
            const data = await dispatch(fetchUserClientAssignments(userId))

            /// the cache is keyed by user id, so a late success is still worth keeping
            setAssignmentsByUserId((current) => ({ ...current, [userId]: data }))
        } catch (e) {
            if (currentRequest.current === userId) {
                setError(e)
            }
        } finally {
            if (currentRequest.current === userId) {
                setLoading(false)
            }
        }
    }

    /// fetch when the modal opens rather than when it mounts -- most rows are never clicked
    useEffect(() => {
        if (!open || !user?.id) {
            return
        }

        currentRequest.current = user.id

        /// already fetched -- clear any spinner or error left behind by a different row
        if (assignmentsByUserId[user.id]) {
            setLoading(false)
            setError(null)

            return
        }

        load(user.id)
    }, [open, user?.id]) // eslint-disable-line

    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ")

    /// The endpoint won't exist until the API change ships, so 404 gets its own wording rather than
    /// being lumped in with a genuine server failure.
    const errorMessage = () => {
        switch (error?.response?.status) {
            case 403:
                return "You don't have access to user assignments."
            case 404:
                return "This lookup isn't available yet."
            default:
                return "Couldn't load this user's assignments."
        }
    }

    const renderRoles = () => {
        const roles = assignments?.roles || []

        if (!roles.length) {
            return <span className="uca-empty">No roles</span>
        }

        return roles.map((role) => (
            /// titleize throws on a missing string, so fall back to the raw value
            <Tag key={role.id || role.name}>{role.name ? titleize(role.name) : "Unknown role"}</Tag>
        ))
    }

    const renderClient = (c) => {
        return (
            <div className="uca-client" key={c.id}>
                {/* new tab on purpose -- navigating in place would discard the report list, its
                    filters and its pagination behind the modal */}
                <Link to={`/clients/${c.id}/dashboard`} target="_blank" rel="noopener noreferrer">
                    {/* the name already leads with the client code -- "088 CRH MidSouth" -- so
                        prefixing `identifier` here rendered it twice */}
                    {c.name || `Client ${c.id}`}
                </Link>
                {c.active === false && <span className="uca-inactive">(inactive)</span>}
            </div>
        )
    }

    const renderClients = () => {
        /// super admins and MUC finance users reach every client, so the server sends no list --
        /// enumerating one would be misleading
        if (assignments?.global_access) {
            return (
                <div className="uca-section">
                    <h4>Clients</h4>
                    <div className="uca-global">All clients</div>
                </div>
            )
        }

        const clients = assignments?.clients || []

        return (
            <div className="uca-section">
                <h4>Clients ({clients.length})</h4>
                {clients.length ? <div className="uca-client-list">{clients.map(renderClient)}</div> : <span className="uca-empty">No client assignments</span>}
            </div>
        )
    }

    const renderBody = () => {
        if (loading) {
            return <Skeleton active loading paragraph={{ rows: 4 }} />
        }

        if (error) {
            return (
                <div className="uca-error">
                    <p>{errorMessage()}</p>
                    <Button type="link" className="no-bg" onClick={() => load(user.id)}>
                        Retry
                    </Button>
                </div>
            )
        }

        if (!assignments) {
            return null
        }

        return (
            <>
                <div className="uca-section">
                    <h4>Roles</h4>
                    <div className="uca-roles">{renderRoles()}</div>
                </div>
                {renderClients()}
            </>
        )
    }

    return (
        <Modal
            open={open}
            className="user-client-assignments-modal"
            onCancel={onClose}
            footer={[
                <Button key="close" type="back" onClick={onClose}>
                    Close
                </Button>,
            ]}>
            <div className="uca-header">
                <h3>{fullName || (user?.id ? `User ${user.id}` : "User")}</h3>
                {user?.email && <div className="uca-email">{user.email}</div>}
            </div>
            {renderBody()}
        </Modal>
    )
}
